import { localDate } from '../dates';
import { singletonId } from '../ids';
import type { Mutation } from '../mutations';
import type { BuildCtx } from '../spending/types';
import { validatePrice } from './price';
import { editTaper, MAX_PER_DAY, validateTaper, type TaperDraft, type TaperError } from './taper';
import { MAX_TRIGGER_PLAN_LENGTH, type Trigger } from './triggers';
import { quitMutation, type Price, type QuitPlan, type QuitResult } from './types';

export interface PlanDraft {
  baselinePerDay: number;
  taper: TaperDraft;
  price: Price;
  fundId: string | null;
  quitlineCountry: string | null;
}

export type PlanError = 'bad_baseline' | TaperError | 'bad_price';

/** The plan is a per-user singleton (conventions §3), so two offline devices converge on one row. */
export function quitPlanId(userId: string): string {
  return singletonId(userId, 'quit_plans');
}

export function priceColumns(p: Price): Record<string, unknown> {
  return {
    priceKind: p.kind,
    packPriceMinor: p.kind === 'pack' ? p.packPriceMinor : null,
    perPack: p.kind === 'pack' ? p.perPack : null,
    stickPriceMinor: p.kind === 'stick' ? p.stickPriceMinor : null,
  };
}

/** Drizzle property names of quit_plans (minus the reserved keys). */
function planValues(p: QuitPlan): Record<string, unknown> {
  return {
    substance: 'cigarettes',
    baselinePerDay: p.baselinePerDay,
    startDate: p.startDate,
    taper: p.taper,
    ...priceColumns(p.price),
    currency: 'PHP',
    fundId: p.fundId,
    triggerPlans: p.triggerPlans,
    quitlineCountry: p.quitlineCountry,
  };
}

/** Creates the plan (starting today) or edits it. An edit writes only changed fields; a schedule edit appends a segment. */
export function buildSavePlan(
  userId: string,
  existing: QuitPlan | null,
  draft: PlanDraft,
  ctx: BuildCtx,
): QuitResult<{ plan: QuitPlan; mutation: Mutation | null }, PlanError> {
  const b = draft.baselinePerDay;
  if (!Number.isInteger(b) || b < 1 || b > MAX_PER_DAY) return { ok: false, error: 'bad_baseline' };
  const taperError = validateTaper(draft.taper);
  if (taperError) return { ok: false, error: taperError };
  if (validatePrice(draft.price)) return { ok: false, error: 'bad_price' };

  const today = localDate(ctx.now, ctx.tz);
  const id = quitPlanId(userId);
  if (!existing) {
    const plan: QuitPlan = {
      id,
      baselinePerDay: b,
      startDate: today,
      taper: [{ from: today, ...draft.taper }],
      price: draft.price,
      fundId: draft.fundId,
      triggerPlans: {},
      quitlineCountry: draft.quitlineCountry,
    };
    return {
      ok: true,
      plan,
      mutation: quitMutation(ctx, 'quit_plans', 'insert', id, planValues(plan)),
    };
  }

  const plan: QuitPlan = {
    ...existing,
    baselinePerDay: b,
    taper: editTaper(existing.taper, today, draft.taper),
    price: draft.price,
    fundId: draft.fundId,
    quitlineCountry: draft.quitlineCountry,
  };
  const before = planValues(existing);
  const after = planValues(plan);
  const changed: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(after)) {
    if (JSON.stringify(v) !== JSON.stringify(before[k])) changed[k] = v;
  }
  const mutation =
    Object.keys(changed).length > 0 ? quitMutation(ctx, 'quit_plans', 'update', id, changed) : null;
  return { ok: true, plan, mutation };
}

/** The user's own plan for a trigger; empty text goes back to the default. */
export function buildSetTriggerPlan(
  plan: QuitPlan,
  trigger: Trigger,
  text: string,
  ctx: BuildCtx,
): QuitResult<{ mutation: Mutation }, 'too_long'> {
  const clean = text.trim().replace(/\s+/g, ' ');
  if (clean.length > MAX_TRIGGER_PLAN_LENGTH) return { ok: false, error: 'too_long' };
  const next = { ...plan.triggerPlans };
  if (clean === '') delete next[trigger];
  else next[trigger] = clean;
  return {
    ok: true,
    mutation: quitMutation(ctx, 'quit_plans', 'update', plan.id, { triggerPlans: next }),
  };
}
